import apiV2 from '../api/v2';
import { UserFacingError } from '../utils/userFacingError';

export interface EventImageUploadResponse {
    fileId: string;
    objectPath: string;
    url: string;
    expiresAt: string;
}

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
// The resolve endpoint caps its body at 50 ids.
const RESOLVE_BATCH_SIZE = 50;

// Mirrors the server-side checks so a bad pick is rejected in the picker
// instead of after a 5MB round-trip. null = acceptable.
export function imageRejectionReason(file: File): string | null {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return 'Image must be JPEG, PNG, or WebP.';
    if (file.size > MAX_IMAGE_BYTES) return 'Image exceeds the 5MB size limit.';
    return null;
}

export async function uploadEventImage(file: File): Promise<EventImageUploadResponse> {
    const rejection = imageRejectionReason(file);
    if (rejection) throw new UserFacingError(rejection);

    const form = new FormData();
    form.append('image', file, file.name);

    const response = await apiV2.post<{ success: true; data: EventImageUploadResponse }>(
        '/events/images',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data.data;
}

// Signed URLs, never persisted: store the fileId and re-resolve. Unknown or
// foreign ids are simply absent from the map rather than failing the batch.
export async function resolveEventImageUrls(fileIds: string[]): Promise<Record<string, string>> {
    if (fileIds.length === 0) return {};

    const batches: string[][] = [];
    for (let i = 0; i < fileIds.length; i += RESOLVE_BATCH_SIZE) {
        batches.push(fileIds.slice(i, i + RESOLVE_BATCH_SIZE));
    }

    const results = await Promise.all(
        batches.map((batch) =>
            apiV2.post<{ success: true; data: { urls: Record<string, string> } }>(
                '/events/images/resolve',
                { fileIds: batch }
            )
        )
    );

    return results.reduce<Record<string, string>>(
        (urls, response) => ({ ...urls, ...(response.data?.data?.urls ?? {}) }),
        {}
    );
}

// Best-effort cleanup of an image the announcement no longer points at; the
// caller decides whether a failure matters (a save that already succeeded
// does not).
export async function deleteEventImage(fileId: string): Promise<void> {
    await apiV2.delete(`/events/images/${encodeURIComponent(fileId)}`);
}
